import { HTMLProps, useState } from 'react'
import clsx from 'classnames'
import { motion } from 'framer-motion'
import { useTranslations } from 'next-intl'
import { useLocale } from 'use-intl'
import { PinnedEvent } from '@/entities/pinned-event'
import { EventCard } from '@/entities/event-card'
import { containerVariants, getCategoryId, useAppearance, useScaleSection } from '@/shared/utils'
import { FiltersBar } from '@/shared/ui/filters-bar'
import { Button } from '@/shared/ui'
import { IEventCategory, IEventPreview } from '@/interfaces/events'
import { localeMap } from '@/shared/utils/directus'
import getEvents, { GetEventsParams } from '@/actions/getEvents'
import style from './main-events.module.scss'

interface EventsProps extends HTMLProps<HTMLDivElement> {
  loaded?: boolean
  events: IEventPreview[]
  eventCategories: IEventCategory[]
  pinnedEvent: IEventPreview
}

const AllEventsBtn = ({ className }: HTMLProps<HTMLButtonElement | HTMLDivElement>) => {
  const translate = useTranslations()
  return (
    <Button className={className} href="/events">
      {translate('buttonAllEvents')}
    </Button>
  )
}

export const MainEvents = (props: EventsProps) => {
  const { className, loaded, events, eventCategories, pinnedEvent } = props
  const { ref: eventsRef, animate: eventsAnimate } = useAppearance()

  const classNames = clsx(className, style.events, 'main-padding trigger-js', {
    [style.loaded]: loaded,
  })
  const currentLocale = useLocale()
  const sectionRef = useScaleSection(loaded)
  const [cards, setCards] = useState<IEventPreview[]>(events)
  const fetchData = ({ categoryId }: GetEventsParams) => {
    getEvents({ locale: localeMap[currentLocale], categoryId, limit: 3, isPinned: false })
      .then(({ items, total, limit }) => {
        setCards(items)
      })
      .catch(error => {
        console.error(error)
      })
  }
  const onChangeCategory = (filter: IEventCategory | null) => {
    const categoryId = getCategoryId(filter)
    const sort = filter?.sort_children_by ? [filter.sort_children_by] : undefined

    fetchData({ categoryId, sort })
  }

  return (
    <section id="events" className={classNames} ref={sectionRef}>
      <FiltersBar
        className={style.filters}
        filters={eventCategories}
        onFilterChange={onChangeCategory}
        renderButton={AllEventsBtn}
        withoutAll
      />
      <div className={style.content}>
        <PinnedEvent className={style.mainEvent} {...pinnedEvent} />
        <motion.ul
          className={style.cards}
          ref={eventsRef}
          initial="hidden"
          animate={eventsAnimate}
          variants={containerVariants}
        >
          {cards.map(eventItem => (
            <EventCard className={style.event} key={eventItem.id} {...eventItem} />
          ))}
        </motion.ul>
      </div>
      <AllEventsBtn className={style.mobileLink} />
    </section>
  )
}
